import { useState } from 'react';
import {
  AlertDialog,
  Button,
  Chip,
  Description,
  Dropdown,
  Label,
  Modal,
  Spinner,
  Table,
  toast,
} from '@heroui/react';
import { IconDots, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { trpc } from '@/lib/trpc';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';
import {
  GRANT_MODE_LABELS,
  SkuEntitlementTemplateEditorModal,
  type SkuEntitlementTemplateFormItem,
} from './SkuEntitlementTemplateEditorModal';

interface SkuEntitlementTemplateModalProps {
  open: boolean;
  skuId?: string | null;
  skuName?: string;
  onCancel: () => void;
}

type EntitlementRow = SkuEntitlementTemplateFormItem & {
  id: string;
};

function dash(value: unknown): string {
  if (value == null || value === '') return '-';
  return String(value);
}

function formatQuantity(value: number | string | null | undefined): string {
  if (value == null || value === '') return '-';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return dash(value);
  return new Intl.NumberFormat('zh-CN').format(n);
}

function grantModeLabel(value: string | null | undefined): string {
  if (!value) return '-';
  return GRANT_MODE_LABELS[value as keyof typeof GRANT_MODE_LABELS] ?? value;
}

function multiplyBySeatLabel(value: boolean | number | null | undefined): string {
  return value === true || Number(value) === 1 ? '是' : '否';
}

export function SkuEntitlementTemplateModal({
  open,
  skuId,
  skuName,
  onCancel,
}: SkuEntitlementTemplateModalProps) {
  const utils = trpc.useUtils();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<SkuEntitlementTemplateFormItem | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<EntitlementRow | null>(null);

  const { data: items = [], isLoading } = trpc.bizProduct.skuEntitlementTemplate.listAll.useQuery(
    { skuId: skuId ?? undefined, sortBy: 'createdAt', sortOrder: 'asc' },
    { enabled: open && Boolean(skuId) }
  );

  const deleteMutation = trpc.bizProduct.skuEntitlementTemplate.delete.useMutation({
    onSuccess: () => {
      toast.success('权益已删除');
      setDeleteOpen(false);
      setItemToDelete(null);
      void utils.bizProduct.skuEntitlementTemplate.listAll.invalidate();
    },
    onError: (error) => {
      toast.danger('删除失败', { description: error.message });
    },
  });

  const rows = items as EntitlementRow[];

  const handleCreate = () => {
    setEditingItem(null);
    setEditorOpen(true);
  };

  const handleEdit = (row: EntitlementRow) => {
    setEditingItem(row);
    setEditorOpen(true);
  };

  const handleDelete = (row: EntitlementRow) => {
    setItemToDelete(row);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete?.id) return;
    await deleteMutation.mutateAsync({ id: itemToDelete.id });
  };

  const resourceName = (row: EntitlementRow) =>
    dash(row.resourceDefinition?.name);
  const resourceCode = (row: EntitlementRow) =>
    dash(row.resourceDefinition?.code);

  return (
    <>
      <Modal isOpen={open} onOpenChange={(next) => !next && onCancel()}>
        <RacDialogTriggerAnchor />
        <Modal.Backdrop>
          <Modal.Container placement="center" scroll="inside">
            <Modal.Dialog className="min-h-[50vh] min-w-[50vw] max-h-[90vh] max-w-[90vw]">
              <Modal.CloseTrigger />
              <Modal.Header className="gap-1">
                <Modal.Heading>权益配置</Modal.Heading>
                <Description className="text-sm">
                  SKU：{skuName || '-'}
                </Description>
              </Modal.Header>
              <Modal.Body className="flex flex-col gap-4">
                <div className="flex items-center justify-end">
                  <Button size="sm" isDisabled={!skuId} onPress={handleCreate}>
                    <IconPlus className="size-4" stroke={1.5} />
                    添加权益
                  </Button>
                </div>

                {isLoading ? (
                  <div className="flex justify-center py-10">
                    <Spinner />
                  </div>
                ) : (
                  <Table>
                    <Table.ScrollContainer>
                      <Table.Content aria-label="权益模板列表" className="min-w-[720px]">
                        <Table.Header>
                          <Table.Column isRowHeader>资源名称</Table.Column>
                          <Table.Column>资源代码</Table.Column>
                          <Table.Column>发放方式</Table.Column>
                          <Table.Column>单位发放量</Table.Column>
                          <Table.Column>乘以席位</Table.Column>
                          <Table.Column>操作</Table.Column>
                        </Table.Header>
                        <Table.Body>
                          {rows.length === 0 ? (
                            <Table.Row id="empty">
                              <Table.Cell>尚未进行配置</Table.Cell>
                              <Table.Cell>-</Table.Cell>
                              <Table.Cell>-</Table.Cell>
                              <Table.Cell>-</Table.Cell>
                              <Table.Cell>-</Table.Cell>
                              <Table.Cell>-</Table.Cell>
                            </Table.Row>
                          ) : (
                            rows.map((row) => (
                              <Table.Row key={String(row.id)} id={String(row.id)}>
                                <Table.Cell>{resourceName(row)}</Table.Cell>
                                <Table.Cell>{resourceCode(row)}</Table.Cell>
                                <Table.Cell>
                                  <Chip size="sm" variant="soft">
                                    <Chip.Label>{grantModeLabel(row.grantMode)}</Chip.Label>
                                  </Chip>
                                </Table.Cell>
                                <Table.Cell>{formatQuantity(row.baseQuantity)}</Table.Cell>
                                <Table.Cell>{multiplyBySeatLabel(row.isMultiplyBySeat)}</Table.Cell>
                                <Table.Cell>
                                  <Dropdown>
                                    <Button
                                      isIconOnly
                                      aria-label="操作"
                                      size="sm"
                                      variant="ghost"
                                    >
                                      <IconDots className="size-4" stroke={1.5} />
                                    </Button>
                                    <Dropdown.Popover
                                      className="min-w-[140px]"
                                      placement="bottom end"
                                    >
                                      <Dropdown.Menu>
                                        <Dropdown.Item
                                          id="edit"
                                          textValue="编辑"
                                          onAction={() => handleEdit(row)}
                                        >
                                          <IconPencil
                                            className="size-4 shrink-0 text-muted"
                                            stroke={1.5}
                                          />
                                          <Label>编辑</Label>
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                          id="delete"
                                          textValue="删除"
                                          variant="danger"
                                          onAction={() => handleDelete(row)}
                                        >
                                          <IconTrash className="size-4 shrink-0" stroke={1.5} />
                                          <Label>删除</Label>
                                        </Dropdown.Item>
                                      </Dropdown.Menu>
                                    </Dropdown.Popover>
                                  </Dropdown>
                                </Table.Cell>
                              </Table.Row>
                            ))
                          )}
                        </Table.Body>
                      </Table.Content>
                    </Table.ScrollContainer>
                  </Table>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onPress={onCancel}>
                  关闭
                </Button>
              </Modal.Footer>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>

      {skuId ? (
        <SkuEntitlementTemplateEditorModal
          open={editorOpen}
          skuId={skuId}
          skuName={skuName}
          item={editingItem}
          onCancel={() => {
            setEditorOpen(false);
            setEditingItem(null);
          }}
          onSuccess={() => {
            setEditorOpen(false);
            setEditingItem(null);
          }}
        />
      ) : null}

      <AlertDialog isOpen={deleteOpen} onOpenChange={setDeleteOpen}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除资源「
                {itemToDelete?.resourceDefinition?.name ??
                  itemToDelete?.resourceDefinition?.code ??
                  '该权益'}
                」的配置吗？
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button
                  variant="secondary"
                  onPress={() => {
                    setDeleteOpen(false);
                    setItemToDelete(null);
                  }}
                >
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deleteMutation.isPending}
                  onPress={() => void confirmDelete()}
                >
                  {deleteMutation.isPending ? '删除中…' : '确认删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </>
  );
}
